/* eslint-disable no-unused-vars */
/* global React */
import { useMemoRef, useStyles } from '@platform/react/hook'
import { Box, Grid } from '@mui/material'
import FileImage from 'ui/Element/Attachment/Item/File'
import Icon from 'ui/Element/Icon'
import { useCallback, useMemo, useState } from 'react'
import Text from 'ui/Element/Text'
import ProgressBar from 'ui/Element/Progress/Bar'
import { isRemote } from 'platform/adapter/attachment/helper'
import { formatFilesize, getMimeType } from 'lib/util/attachment'
import { makeBreakable } from 'lib/util/string'

/**
 * Returns the AttachmentListTemplateAction element's current theme styles.
 *
 * @param {Object} theme  the application's current theme
 * @returns {Object}      the AttachmentListTemplateAction component's styles
 */
const styles = theme => ({
  root: {
    height: '4rem',
    position: 'relative',
    justifyContent: 'inherit',

    '&:not(:last-child)': {
      paddingBottom: theme.spacing(0.4),
    },
  },
  delete: {
    position: 'absolute',
    top: 'calc(-1.25rem / 2)', // same as height
    right: 'calc(-1.25rem / 2)', // same as width
    zIndex: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.25rem',
    height: '1.25rem',
    borderRadius: '50%',
    backgroundColor: theme.palette.black.main,
  },
  wrapper: {
    gap: '0.625rem',
    display: 'flex',
    position: 'relative',
    flexWrap: 'nowrap',
    alignItems: 'center',
    boxShadow: `inset 0 0 0 0.0625rem ${theme.palette.gray[900]}`,
    borderRadius: '0.5rem',
    padding: '0.5rem',
    overflow: 'hidden',
  },
  image: {
    height: '3rem',
    width: '3rem',
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  text: {
    display: 'inline-block',
    whiteSpace: 'nowrap !important',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  infoText: {
    textTransform: 'uppercase',
  },
  progressContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
  },
  ...theme.custom.attachmentListTemplateAction,
})

/**
 * AttachmentListTemplateAction attachment component
 *
 * Displays a clickable representation of a file that would allow a specific action to be performed,
 * such as playing it in the case of a video or audio file, or downloading it.
 *
 * @param {Object} props
 * @param {Object} props.attachment     the attached file
 * @param {Object} props.types          the types for the {@link FileImage} element
 * @param {boolean} props.showProgress  whether to show a progress bar
 * @param {Object} props.classes        styling
 * @param {string} [props.group]        attachment group
 */
const AttachmentListTemplateAction = ({
  attachment,
  types,
  showProgress = true,
  showDelete = true,
  classes: parentClasses,
  group,
  ...props
}) => {
  const { events, download = true, placeholderProps } = props
  const { value: item, upload } = attachment
  const [attachmentState, setAttachmentState] = useState({})
  const { progress: downloadProgress = 0 } = attachmentState || {}
  const progress = upload ? upload.progress : downloadProgress
  const indeterminate = !!upload && (progress <= 0 || progress >= 100)

  const classes = { ...useStyles(styles)(), ...parentClasses }
  const sanitizedName = useMemo(() => decodeURIComponent(item.name), [item.name])

  const info = useMemo(() => {
    const mimeType = getMimeType(item)
    const [, fileExtension] = item?.name?.split?.('.') || []
    const typeInfo = mimeType === 'other' ? fileExtension : mimeType
    const type = typeInfo.length > 10 ? `${typeInfo.slice(0, 10)}...` : typeInfo
    const size = formatFilesize(item?.size)
    return [type, size].filter(x => !!x).join(' • ')
  }, [item])

  const handleDelete = useCallback(events?.onDelete?.(group, attachment), [attachment])
  const canDelete = useMemo(
    () => events?.onDelete && (attachment?.acl?.includes?.(40) || !isRemote(attachment) || false),
    [attachment],
  )

  return (
    <Grid
      item
      container
      xs={12}
      sm={12}
      md={12}
      lg={12}
      xl={12}
      title={sanitizedName}
      className={classes.root}
    >
      {showDelete && canDelete && (
        <Box
          className={classes.delete}
          onClick={handleDelete}
        >
          <Icon
            name={'close'}
            size={'0.75rem'}
            color={'white.main'}
          />
        </Box>
      )}
      <Grid
        item
        xs={12}
        container
        className={classes.wrapper}
      >
        <Grid
          item
          xs={'auto'}
          className={classes.image}
        >
          <FileImage
            types={types}
            group={group}
            download={download}
            placeholderProps={placeholderProps}
            previewableTypes={['image']}
            attachment={attachment}
            events={{
              onClick: events?.onClick,
              onState: setAttachmentState,
            }}
          />
        </Grid>
        <Grid
          item
          style={{ flex: 1, minWidth: 0 }}
        >
          <Text
            maxLines={1}
            variant={'14/regular'}
            color={'black.main'}
            value={makeBreakable(sanitizedName)}
          />
          {info && (
            <Text
              variant={'12/regular'}
              color={'gray.500'}
              maxLines={1}
              value={makeBreakable(info)}
              className={classes.infoText}
            />
          )}
        </Grid>
        {showProgress && (
          <ProgressBar
            value={progress}
            color={'pending.700'}
            indeterminate={indeterminate}
            containerStyle={classes.progressContainer}
          />
        )}
      </Grid>
    </Grid>
  )
}

export default useMemoRef(AttachmentListTemplateAction, props => [
  props.classes,
  props.attachment,
])
